import { ticketListQuerySchema, type TicketListQuery } from '@gb-ticket/shared-types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  EMPTY_TICKET_LIST_QUERY,
  ticketListQueryFromSearch,
} from '@/products/helpdesk/features/tickets/ticket-list-path';

export { EMPTY_TICKET_LIST_QUERY };

type TicketListFilterScope = 'agent' | 'portal';

type StoredTicketListFilters = {
  searchInput: string;
  query: TicketListQuery;
};

function storageKey(scope: TicketListFilterScope, userId: string) {
  return `gb-ticket.ticket-list-filters.${scope}.${userId}`;
}

function loadStored(scope: TicketListFilterScope, userId: string): StoredTicketListFilters | null {
  try {
    const raw = window.localStorage.getItem(storageKey(scope, userId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { searchInput?: unknown; query?: unknown };
    const query = ticketListQuerySchema.parse({
      ...EMPTY_TICKET_LIST_QUERY,
      ...(parsed.query && typeof parsed.query === 'object' ? parsed.query : {}),
    });
    return {
      searchInput: typeof parsed.searchInput === 'string' ? parsed.searchInput : (query.q ?? ''),
      query,
    };
  } catch {
    return null;
  }
}

function saveStored(scope: TicketListFilterScope, userId: string, value: StoredTicketListFilters) {
  try {
    window.localStorage.setItem(storageKey(scope, userId), JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures; filters still work in-session.
  }
}

function hydrateFilters(
  scope: TicketListFilterScope,
  userId: string | undefined,
  searchKey: string,
): StoredTicketListFilters & { hydratedFor: string | null } {
  if (!userId) {
    return {
      searchInput: '',
      query: EMPTY_TICKET_LIST_QUERY,
      hydratedFor: null,
    };
  }
  const fromUrl = ticketListQueryFromSearch(searchKey);
  const stored = loadStored(scope, userId);
  if (fromUrl) {
    return {
      query: fromUrl,
      searchInput: fromUrl.q ?? '',
      hydratedFor: userId,
    };
  }
  if (stored) {
    return { ...stored, hydratedFor: userId };
  }
  return {
    query: EMPTY_TICKET_LIST_QUERY,
    searchInput: '',
    hydratedFor: userId,
  };
}

export function usePersistedTicketListQuery(
  scope: TicketListFilterScope,
  userId: string | undefined,
) {
  const [searchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const hydrateKey = `${scope}:${userId ?? ''}:${searchKey}`;

  const [searchInput, setSearchInput] = useState(
    () => hydrateFilters(scope, userId, searchKey).searchInput,
  );
  const [query, setQuery] = useState<TicketListQuery>(
    () => hydrateFilters(scope, userId, searchKey).query,
  );
  const [hydratedFor, setHydratedFor] = useState<string | null>(
    () => hydrateFilters(scope, userId, searchKey).hydratedFor,
  );
  const [prevHydrateKey, setPrevHydrateKey] = useState(hydrateKey);

  // Re-hydrate when user, scope, or URL search changes (React: adjust state while rendering).
  if (hydrateKey !== prevHydrateKey) {
    setPrevHydrateKey(hydrateKey);
    const next = hydrateFilters(scope, userId, searchKey);
    setQuery(next.query);
    setSearchInput(next.searchInput);
    setHydratedFor(next.hydratedFor);
  }

  const ready = Boolean(userId && hydratedFor === userId);

  useEffect(() => {
    if (!userId || hydratedFor !== userId) {
      return;
    }
    saveStored(scope, userId, { searchInput, query });
  }, [hydratedFor, userId, scope, searchInput, query]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    const handle = window.setTimeout(() => {
      setQuery((current) => {
        const next = searchInput.trim() || undefined;
        if (current.q === next) {
          return current;
        }
        return { ...current, q: next, page: 1 };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput, ready]);

  function patchQuery(patch: Partial<TicketListQuery>) {
    setQuery((current) => ({ ...current, ...patch }));
  }

  return {
    query,
    setQuery,
    patchQuery,
    searchInput,
    setSearchInput,
    ready,
  };
}
